import { apiSlice } from '@/redux/api.slice';
import {
  HomeSliderResponse,
  HomeSliderSettings,
  MiscType,
  CategorySushiBar,
  HighlightsNav
} from './home.types';
import { setNavigationData, setSliderData } from './home.slice';
import { transformHomeSliderData } from '@/lib/transform-response';

export const homeApi = apiSlice.injectEndpoints({
  // Dev-only: silences the "override without overrideExisting" warning that
  // Next dev's module re-evaluation triggers against the shared apiSlice
  // singleton — see the identical comment in redux/auth/auth.api.ts.
  overrideExisting: process.env.NODE_ENV !== 'production',
  endpoints: (builder) => ({
    getSlider: builder.query({
      query: () => {
        return {
          url: 'api/home/slider',
          method: 'GET'
        };
      },
      transformResponse: (response: {
        data: HomeSliderResponse[];
        status: string;
      }) => {
        return {
          slides: response.data?.map(transformHomeSliderData),
          status: response.status
        };
      },
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled;

          if (result?.data?.status === 'success') {
            dispatch(setSliderData(result.data.slides));
          }
        } catch {
          // Already reported by the baseQuery in api.slice.ts.
        }
      },
      providesTags: ['HomeSlider']
    }),
    // Own endpoint (not folded into getSlider) so the existing array-shaped
    // slider response stays untouched for every consumer of that endpoint.
    getSliderSettings: builder.query<HomeSliderSettings, void>({
      query: () => 'api/home/slider-settings',
      transformResponse: (response: {
        data: HomeSliderSettings;
        status: string;
      }) => response.data,
      providesTags: ['HomeSlider']
    }),
    getNavigation: builder.query({
      query: () => 'api/home/navigation',
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const result = await queryFulfilled;

          if (result?.data?.status === 'success') {
            dispatch(setNavigationData(result.data.data));
          }
        } catch {
          // Already reported by the baseQuery in api.slice.ts.
        }
      },
      providesTags: ['Navigation']
    }),
    getTopRetailers: builder.query({
      query: () => 'api/home/top-retailers'
    }),
    getTopDealsSection: builder.query({
      query: () => 'api/home/sections',
      providesTags: ['TopDeals']
    }),
    // Already filtered server-side by active flag, validity window and (for
    // internal banners) deal visibility — the client only buckets by anchor.
    getHomeBanners: builder.query({
      query: () => 'api/home/banners',
      providesTags: ['HomeBanners']
    }),
    getCategorySushiBar: builder.query<CategorySushiBar, void>({
      query: () => 'api/home/category-sushi-bar',
      transformResponse: (response: {
        data: CategorySushiBar;
        status: string;
      }) => response.data,
      providesTags: ['CategorySushiBar']
    }),
    // Deliberately separate from getNavigation: that endpoint returns a bare
    // category array which both the nav selector and sitemap.ts consume, so
    // widening it would be a breaking change for two callers. This one is a
    // two-field payload the server caches for 6h.
    getHighlightsNav: builder.query<HighlightsNav, void>({
      query: () => 'api/highlights/nav',
      transformResponse: (response: { data: HighlightsNav }) => response.data,
      providesTags: ['HighlightsNav']
    }),
    // Own endpoint (not getHomeBanners) — filtered and cached separately on the
    // server since it's a different anchor pool (HIGHLIGHTS_BEFORE_ROW_*).
    // Already filtered server-side by active flag, validity window and (for
    // internal banners) deal visibility, same as getHomeBanners.
    getHighlightsBanners: builder.query({
      query: () => 'api/highlights/banners',
      providesTags: ['HighlightsBanners']
    }),
    getMiscContent: builder.query({
      query: (miscType: MiscType) => `api/footer/misc/${miscType}`
    }),
    postContact: builder.mutation({
      query: (data) => ({
        url: 'api/footer/contact',
        method: 'POST',
        body: data
      })
    })
  })
});

export const {
  useGetSliderQuery,
  useGetSliderSettingsQuery,
  useGetNavigationQuery,
  useGetTopRetailersQuery,
  useGetTopDealsSectionQuery,
  useGetHomeBannersQuery,
  useGetCategorySushiBarQuery,
  useGetHighlightsNavQuery,
  useGetHighlightsBannersQuery,
  useGetMiscContentQuery,
  usePostContactMutation
} = homeApi;
